<template>
	<div class="row" style="position:relative; min-height:110px">
		<img v-if="!data_loaded" class="center-me" src="/images/loading-color.svg">

		<template v-else>
			<div class="spark-chart col col-lg-3 col-md-6">
				<div class="card-header p-0 border-none">New Physicians</div>
				<div class="mt-2 d-flex align-items-center">
					<div class="text-5xl font-weight-bold mr-2">{{ metrics.physicians.count }}</div>
					<div class="text-muted leading-none">
						<span :class="metrics.physicians.pointer">
							<i class="fas" :class="metrics.physicians.pointer === 'text-negative' ? 'fa-caret-down' : 'fa-caret-up'"></i> 
							{{ Math.abs(metrics.physicians.change) }}
							({{ metrics.physicians.percent ? metrics.physicians.percent : 'n/a' }}%)
						</span>					
						<br>
						vs prior week
					</div>
				</div>	
				<div>
					<line-chart :chart-data="chartData.physicians" :options="chartOptions" :height="40"></line-chart>
				</div>	
			</div>

			<div class="spark-chart col col-lg-3 col-md-6">
				<div class="card-header p-0 border-none">Checkins</div>
				<div class="mt-2 d-flex align-items-center">
					<div class="text-5xl font-weight-bold mr-2">{{ metrics.checkins.count }}</div>
					<div class="text-muted leading-none">
						<span :class="metrics.checkins.pointer">
							<i class="fas" :class="metrics.checkins.pointer === 'text-negative' ? 'fa-caret-down' : 'fa-caret-up'"></i> 
							{{ Math.abs(metrics.checkins.change) }}
							({{ metrics.checkins.percent ? metrics.checkins.percent : 'n/a' }}%)
						</span>					
						<br>
						vs prior week
					</div>
				</div>	
				<div>
					<line-chart :chart-data="chartData.checkins" :options="chartOptions" :height="40"></line-chart>
				</div>	
			</div>

			<div class="spark-chart col col-lg-3 col-md-6">
				<div class="card-header p-0 border-none">Pt. Referrals</div>
				<div class="mt-2 d-flex align-items-center">
					<div class="text-5xl font-weight-bold mr-2">{{ metrics.referrals.count }}</div>
					<div class="text-muted leading-none">
						<span :class="metrics.referrals.pointer">
							<i class="fas" :class="metrics.referrals.pointer === 'text-negative' ? 'fa-caret-down' : 'fa-caret-up'"></i> 
							{{ Math.abs(metrics.referrals.change) }}
							({{ metrics.referrals.percent ? metrics.referrals.percent : 'n/a' }}%)
						</span>					
						<br>
						vs prior week
					</div>
				</div>	
				<div>
					<line-chart :chart-data="chartData.referrals" :options="chartOptions" :height="40"></line-chart>
				</div>	
			</div>

			<div class="spark-chart col col-lg-3 col-md-6">
				<div class="card-header p-0 border-none">Est. Charges</div>
				<div class="mt-2 d-flex align-items-center">
					<div class="text-5xl font-weight-bold mr-2">{{ numeral(metrics.charges.count).format('($0a)') }}</div>
					<div class="text-muted leading-none">
						<span :class="metrics.charges.pointer">
							<i class="fas" :class="metrics.charges.pointer === 'text-negative' ? 'fa-caret-down' : 'fa-caret-up'"></i> 
							{{ numeral(Math.abs(metrics.charges.change)).format('($0a)') }}
							({{ metrics.charges.percent ? metrics.charges.percent : 'n/a' }}%)
						</span>					
						<br>
						vs prior week
					</div>
				</div>	
				<div>
					<line-chart :chart-data="chartData.charges" :options="chartOptions" :height="40"></line-chart>
				</div>	
			</div>
		</template>							
	</div>
</template>

<script>

	import LineChart from '../helpers/LineChart.js';

	export default {
		components: {
			LineChart,
		},

		data() {
			return {
				data_loaded: false,

				chartData: {
					physicians: {},
					checkins: {},
					referrals: {},
					charges: {},
				},

				chartDataDefaults: {
                    backgroundColor: '#FAECFB',
                    borderColor: '#836085',
                    borderWidth: 2,
                    radius: 0,
                    lineTension: .15,
                },

	            chartOptions: {
	                responsive: true,                
	                maintainAspectRatio: false,
	                hover: { 
	                	mode: null
	                },
	                tooltips: {
	                	enabled: false,
	                },
	                title: {
	                    display: false,
	                },                
	                legend: {
	                    display: false
	                },                 
	                animation: {
	                    duration: 0,
	                },                
	                scales: {
	                    xAxes: [{
                            type: 'time',
                            distribution: 'linear',
                            time: {
                                unit: 'week',
                                displayFormats: {
                                    week: 'll'
                                }
                            },  	                    	
	                        display: false,
	                        gridLines: {
	                        	display: false,
	                        },
	                        ticks: {
	                        	display: false,
	                        }
	                    }],
	                    yAxes: [{
	                        display: false, 
	                        ticks: {
	                            min: 0,
	                            beginAtZero: true,
	                        }                        
	                    }],
	                },                
	            },
	            
				metrics: {},				
			}
		},

		created() {
            axios.get(`/api/emr-data/weekly`).then(({data}) => {
                this.setChartData(data);
                this.setMetrics(data);
                this.data_loaded = true;
        	});	
		},

		methods: {		
			setChartData(data) {
                this.chartData.physicians = {
                    datasets: [{
                        ...this.chartDataDefaults,
                        data: Object.keys(data.physicians).map(created => ({ t: created, y: data.physicians[created] })),
                    }]                  
                };

                this.chartData.checkins = {
                    datasets: [{
                        ...this.chartDataDefaults,
                        data: Object.keys(data.checkins).map(created => ({ t: created, y: data.checkins[created] })),
                    }]                  
                };

                this.chartData.referrals = {
                    datasets: [{
                        ...this.chartDataDefaults,
                        data: Object.keys(data.referrals).map(created => ({ t: created, y: data.referrals[created] })),
                    }]                  
                };

                this.chartData.charges = {
                    datasets: [{
                        ...this.chartDataDefaults,
                        data: Object.keys(data.charges).map(created => ({ t: created, y: data.charges[created] })),
                    }]                  
                };
			},

			setMetrics(data) {
				let lastWeek = moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD');
				let prevWeek = moment().subtract(2, 'weeks').startOf('isoWeek').format('YYYY-MM-DD');

				//Null checks for safety here
				this.metrics.physicians = this.calcMetricChange(
					data.physicians[lastWeek] ? data.physicians[lastWeek] : 0,
					data.physicians[prevWeek] ? data.physicians[prevWeek] : 0
				);

				this.metrics.checkins = this.calcMetricChange(
					data.checkins[lastWeek] ? data.checkins[lastWeek] : 0,
					data.checkins[prevWeek] ? data.checkins[prevWeek] : 0
				);

				this.metrics.referrals = this.calcMetricChange(
					data.referrals[lastWeek] ? data.referrals[lastWeek] : 0,
					data.referrals[prevWeek] ? data.referrals[prevWeek] : 0
				);

				this.metrics.charges = this.calcMetricChange(
					data.charges[lastWeek] ? data.charges[lastWeek] : 0,
					data.charges[prevWeek] ? data.charges[prevWeek] : 0
				);
			},

			calcMetricChange(main, comp) {
				return {
					count: main,
					change: main - comp,
					percent: comp == 0 ? 'n/a' : Math.round(((main - comp) / comp) * 100),
					pointer: main > comp ? 'text-positive' : (main < comp ? 'text-negative' : 'text-neutral'),					
				}
			}
		},


	}

</script>

<style scoped>
	.spark-chart {min-width:200px;}
	.card-header.border-none { border-bottom:none; }
</style>